import { ChangeDetectionStrategy,Component, computed, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { MatButton } from "@angular/material/button";
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { blobToBase64 } from 'app/shared/utilities/file-helpers';
import { environment } from 'environments/environment';

import { DesignerCustomizationsService } from './designer-customizations.service';

@Component({
    selector: 'admin-store-designer-logo',
    templateUrl: './store-designer-logo.component.html',
    styleUrls: ['./store-designer-logo.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, SaveButtonComponent, MatIcon, ErrorComponent, MatButton]
})
export class StoreDesignerLogoComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly designerCustomizationsService = inject(DesignerCustomizationsService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    public logoUrl?: string;
    public storeId: string;
    private baseLogoUrl: string;

    @ViewChild('logo')
    public logo: HTMLImageElement;

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.baseLogoUrl = `${environment.hub.hubApiUrl}/stores/${this.storeId}/config/logo`;
            this.subscribeWithGenericLoadingErrorHandling(this.designerCustomizationsService.getStoreFiles(this.storeId), (storeFiles) => {
                this.logoUrl = storeFiles.logo?.url ?? undefined;
                this.pageStatus = PageStatus.loaded;
            });
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    async onFilesChanged(files: FileList) {
        const contentBase64 = await blobToBase64(files[0]);

        this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.updateLogo({
            storeId: this.storeId,
            contentBase64: contentBase64,
            mimeType: files[0].type
        }), () => {
            this.logoUrl = `${this.baseLogoUrl}?t=${new Date().getTime()}`;
        });
    }

    public get canDelete(): Signal<boolean> {
        return computed(() => this.pageStatus === PageStatus.loaded && this.logoUrl !== undefined);
    }

    deleteLogo(): void {
        this.confirmDialogService
            .confirm(
                'Confirmation',
                `Do you want to delete the logo for this store?`
            ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.deleteLogo({
                    storeId: this.storeId
                }), () => {
                    this.logoUrl = undefined;
                });
            }
        );
    }

    protected readonly PageStatus = PageStatus;
}
